import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PincodeInput from "@/components/common/PincodeInput";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const emptyForm = {
  phone: "",
  bank_name: "", branch: "", registered_person: "", bank_location: "", portal_link: "", address: "",
  home_address: "", working_location: "", working_radius_km: 25, kyc_aadhar: "", kyc_pan: "",
  city: "", pincode: "",
};

/**
 * Post-Google signup completion. Only allowed for role='bank' or 'field'.
 * Reads the pending Google profile from sessionStorage.
 */
export default function GoogleSignupComplete() {
  const { role } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [google, setGoogle] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [pinOk, setPinOk] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("google_pending");
    if (!raw) return;
    try { setGoogle(JSON.parse(raw)); } catch { setGoogle(null); }
  }, []);

  if (!["bank", "field"].includes(role)) return <Navigate to="/" replace />;
  if (!google) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <p className="text-slate-600">No pending Google sign-up found.</p>
          <Link to="/login/bank" className="text-blue-600 hover:underline mt-4 inline-block">Back to sign in</Link>
        </div>
      </div>
    );
  }

  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!pinOk) { toast.error("Please enter a valid Rajasthan pincode"); return; }
    setBusy(true);
    try {
      const payload = { ...form, role, email: google.email, name: google.name };
      const { data } = await api.post("/auth/google/complete-signup", payload, { withCredentials: true });
      sessionStorage.removeItem("google_pending");
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate(`/${data.user.role}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-white border border-slate-200 rounded-lg p-8">
        <Link to="/login/bank" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="google-signup-back">
          <ArrowLeft className="h-4 w-4" /> Cancel
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Google verified</p>
            <p className="font-heading text-lg font-semibold text-slate-950">{google.name} · {google.email}</p>
          </div>
        </div>
        <h2 className="font-heading text-2xl font-bold text-slate-950 mb-2">
          Complete your {role === "bank" ? "Bank / NBFC" : "Field Executive"} profile
        </h2>
        <p className="text-sm text-slate-600 mb-6">One-time details to finish setting up your account.</p>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="google-signup-form">
          <div><Label>Phone</Label><Input required value={form.phone} onChange={upd("phone")} data-testid="gs-phone" /></div>

          {role === "bank" && <>
            <div><Label>Bank / NBFC name</Label><Input required value={form.bank_name} onChange={upd("bank_name")} data-testid="gs-bank-name" /></div>
            <div><Label>Branch</Label><Input required value={form.branch} onChange={upd("branch")} data-testid="gs-bank-branch" /></div>
            <div><Label>Registered Person</Label><Input value={form.registered_person} onChange={upd("registered_person")} data-testid="gs-bank-person" /></div>
            <PincodeInput value={form.pincode}
              onChange={(p) => { setForm({ ...form, pincode: p }); setPinOk(false); }}
              onResolved={(r) => { setPinOk(r.isRajasthan); if (r.isRajasthan) setForm({ ...form, pincode: r.pincode, city: r.city, bank_location: r.district }); }}
              testId="gs-bank-pincode" label="Pincode" required />
            <div><Label>City</Label><Input value={form.city} readOnly className="bg-slate-50" data-testid="gs-bank-city" /></div>
            <div><Label>District</Label><Input value={form.bank_location} readOnly className="bg-slate-50" data-testid="gs-bank-district" /></div>
            <div className="md:col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={upd("address")} data-testid="gs-bank-address" /></div>
            <div className="md:col-span-2"><Label>Portal link (optional)</Label><Input value={form.portal_link} onChange={upd("portal_link")} data-testid="gs-bank-portal" /></div>
          </>}

          {role === "field" && <>
            <PincodeInput value={form.pincode}
              onChange={(p) => { setForm({ ...form, pincode: p }); setPinOk(false); }}
              onResolved={(r) => { setPinOk(r.isRajasthan); if (r.isRajasthan) setForm({ ...form, pincode: r.pincode, city: r.city, working_location: r.district }); }}
              testId="gs-field-pincode" label="Working Pincode" required />
            <div><Label>City</Label><Input value={form.city} readOnly className="bg-slate-50" data-testid="gs-field-city" /></div>
            <div><Label>Working District</Label><Input value={form.working_location} readOnly className="bg-slate-50" data-testid="gs-field-district" /></div>
            <div><Label>Working radius (km)</Label><Input type="number" value={form.working_radius_km} onChange={upd("working_radius_km")} data-testid="gs-field-radius" /></div>
            <div className="md:col-span-2"><Label>Home address</Label><Textarea required value={form.home_address} onChange={upd("home_address")} data-testid="gs-field-home" /></div>
            <div><Label>Aadhaar</Label><Input value={form.kyc_aadhar} onChange={upd("kyc_aadhar")} data-testid="gs-field-aadhaar" /></div>
            <div><Label>PAN</Label><Input value={form.kyc_pan} onChange={upd("kyc_pan")} data-testid="gs-field-pan" /></div>
          </>}

          <div className="md:col-span-2 mt-2">
            <Button type="submit" disabled={busy} className="w-full bg-slate-950 hover:bg-slate-800 text-white h-11" data-testid="google-signup-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete account"}
            </Button>
          </div>

          <div className="md:col-span-2 grid grid-cols-2 gap-2">
            {["bank", "field"].map((r) => (
              <Link key={r} to={`/signup/google/${r}`}
                className={`text-center text-xs py-2 rounded-md border ${r === role ? "bg-slate-950 text-white border-slate-950" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                data-testid={`gs-switch-${r}`}>
                {r === "bank" ? "Bank / NBFC" : "Field Executive"}
              </Link>
            ))}
          </div>
        </form>
      </div>
    </div>
  );
}
